import { createContext, useState, useEffect, useContext } from "react";
import { getAllSessionNotifications, getLoggedInUser } from "../api";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
// Create the UserContext
const UserContext = createContext();

// Custom hook to use the UserContext
export const useUserContext = () => useContext(UserContext);

// Provider component to wrap the app and provide the UserContext data
export const UserContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState([]);
  const [unreadNotification, setUnreadNotification] = useState(0);
  const [isLoading, setisLoading] = useState(true);
  const [previousPage, setPreviousPage] = useState("");
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [section, setSection] = useState(false);

  // Function to clear user session
  const clearUserSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("completedSteps");
    sessionStorage.removeItem("path");
    sessionStorage.removeItem("follow_id");
    setUser(null);
    setNotification([]);
    setUnreadNotification(0);
  };

  // Flag to track if logout was manual (to prevent duplicate toast)
  const isManualLogout = () => {
    return localStorage.getItem("manualLogout") === "true";
  };

  const setManualLogout = (value) => {
    if (value) {
      localStorage.setItem("manualLogout", "true");
    } else {
      localStorage.removeItem("manualLogout");
    }
  };

  // Function to validate token periodically
  const validateToken = async () => {
    const token = localStorage.getItem("token");
    if (!token || !user) return;

    try {
      await getLoggedInUser({ token });
      // Token is still valid
    } catch (error) {
      if (error.response?.status === 401) {
        clearUserSession();
        toast.error("Your session has expired. Please login again.");
        navigate("/login");
      }
    }
  };

  // Function to get user data from the token
  const getUserFromToken = async (token) => {
    // Get token if not provided (better separation of concerns)
    const authToken = token || localStorage.getItem("token");

    if (!authToken) {
      console.error("No token available");
      clearUserSession();
      return;
    }

    setisLoading(true);

    try {
      // Assuming your API expects JSON (adjust if FormData is required)
      const userFromToken = await getLoggedInUser({ token: authToken });
      
      // Check for explicit failure status (e.g. "User not found")
      if (userFromToken?.data?.status === false) {
        console.error("User validation failed:", userFromToken.data.message);
        clearUserSession();
        toast.error(userFromToken.data.message || "Invalid session. Please login again.");
        navigate("/login");
        return;
      }

      if (userFromToken?.data?.data) {
        setUser(userFromToken.data.data);
      } else {
        console.error("Invalid user data received");
        clearUserSession();
        toast.error("Invalid session. Please login again.");
        navigate("/login");
      }
    } catch (error) {
      console.error("Failed to fetch user", error);

      // Handle specific error cases
      if (error.response?.status === 401) {
        clearUserSession();
        toast.error("Your session has expired. Please login again.");
        navigate("/login");
      } else if (error.response?.status === 403) {
        clearUserSession();
        toast.error("Access denied. Please login again.");
        navigate("/login");
      } else {
        // For other errors, clear session but don't redirect immediately
        clearUserSession();
        console.error("Authentication error:", error);
      }
    } finally {
      setisLoading(false);
    }
  };

  // Function to handle user logout
  const handleLogout = () => {
    setManualLogout(true);
    clearUserSession();
    localStorage.setItem("navigation", JSON.stringify("/"));
    toast.success("Logout Successfully.");
    navigate("/");
  };

  const fetchNotifications = async () => {
    try {
      const response = await getAllSessionNotifications();
      setNotification(response.data.data);
      setUnreadNotification(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Don't clear session for notification errors, just log them
    }
  };

  // Listen for localStorage changes (when token is removed)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token" && e.newValue === null && user) {
        // Check if this was a manual logout
        if (isManualLogout()) {
          setManualLogout(false);
          setUser(null);
          setNotification([]);
          setUnreadNotification(0);
          return;
        }
        // Token was removed from localStorage, clear user state
        setUser(null);
        setNotification([]);
        setUnreadNotification(0);
        toast.error("Your session has been cleared. Please login again.");
        navigate("/login");
      }
    };

    // Listen for storage events (for cross-tab synchronization)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically if token still exists
    const checkTokenInterval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token && user) {
        // Check if this was a manual logout
        if (isManualLogout()) {
          setManualLogout(false);
          setUser(null);
          setNotification([]);
          setUnreadNotification(0);
          return;
        }
        // Token was removed but user state still exists
        setUser(null);
        setNotification([]);
        setUnreadNotification(0);
        toast.error("Your session has been cleared. Please login again.");
        navigate("/login");
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(checkTokenInterval);
    };
  }, [user, navigate]);

  useEffect(() => {
    return () => {
      setPreviousPage(location.pathname);
    };
  }, [location.pathname]);

  // Set up periodic token validation
  useEffect(() => {
    if (user) {
      // Validate token every 5 minutes
      const interval = setInterval(validateToken, 5 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [user]);

  const token = localStorage.getItem("token");
  useEffect(() => {
    // Check if there's a token in the local storage when the app mounts
    if (token) {
      // Call the function to get user data from the token
      getUserFromToken(token);
      fetchNotifications();
    }

    if (!token) {
      setisLoading(false);
    }
  }, [token]);

  return (
    <UserContext.Provider
      value={{
        isLoading,
        user,
        setUser,
        getUserFromToken,
        handleLogout,
        clearUserSession,
        validateToken,
        previousPage,
        isOpen,
        setIsOpen,
        fetchNotifications,
        notification,
        unreadNotification,
        section,
        setSection,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
